import { useEffect, useLayoutEffect, useState, type ReactNode } from 'react';
import { useStore } from '../../lib/store';
import { QuillLoader } from '../primitives';
import { durations } from '../theme/motion';
import { bootStore, startBootstrap, startupMessage } from './bootstrap';
import { SPLASH_ID } from './bootWatchdog';
import { holdsForSignature, loaderEpoch, signatureRemaining } from './startupHold';
import styles from './StartupGate.module.css';

export interface StartupScreenProps {
  message: string;
  /** How far into its timeline the loader starts (it continues the static splash's). */
  elapsedMs?: number;
  /** Fading out over the app's first frame. */
  leaving?: boolean;
}

/**
 * The cold-start splash (Flutter's separate light-only splash MaterialApp,
 * spec 01 §5.1/§6.1) with the Quill Line loader. Follows the app theme
 * (Flutter's is always light), so a dark launch never flashes white.
 */
export function StartupScreen({ message, elapsedMs, leaving = false }: StartupScreenProps) {
  return (
    <div className={leaving ? `${styles.screen} ${styles.leaving}` : styles.screen} aria-hidden={leaving || undefined}>
      <div className={styles.content}>
        <QuillLoader message={message} elapsedMs={elapsedMs} />
      </div>
    </div>
  );
}

/**
 * Shows the startup screen until the bootstrap finished and the quill wrote
 * its first stroke in full, then fades it out over the app (300 ms). Reduced
 * motion and automated browsers skip the wait and cut straight to the app.
 */
export function StartupGate({ children }: { children: ReactNode }) {
  const state = useStore(bootStore);
  // Read once, before this first frame replaces the static splash.
  const [timing] = useState(() => {
    const now = performance.now();
    const epoch = loaderEpoch(document, now);
    const hold = holdsForSignature();
    return { elapsedMs: now - epoch, holdMs: hold ? signatureRemaining(epoch, now) : 0, fadeMs: hold ? durations.contentReveal : 0 };
  });
  const [signed, setSigned] = useState(timing.holdMs === 0);
  const [faded, setFaded] = useState(false);
  const ready = state.done && signed;

  useEffect(() => {
    void startBootstrap();
  }, []);

  // React's first frame replaces the static splash, continuing its animation.
  useLayoutEffect(() => {
    document.getElementById(SPLASH_ID)?.remove();
  }, []);

  useEffect(() => {
    if (signed) return;
    const timer = setTimeout(() => setSigned(true), timing.holdMs);
    return () => clearTimeout(timer);
  }, [signed, timing.holdMs]);

  useEffect(() => {
    if (!ready || faded) return;
    const timer = setTimeout(() => setFaded(true), timing.fadeMs);
    return () => clearTimeout(timer);
  }, [ready, faded, timing.fadeMs]);

  // The screen keeps its place in the tree, so its animation never restarts.
  const showScreen = !faded && !(ready && timing.fadeMs === 0);
  return (
    <>
      {ready && children}
      {showScreen && <StartupScreen message={startupMessage(state)} elapsedMs={timing.elapsedMs} leaving={ready} />}
    </>
  );
}
